import type { MetagameResultSocketData, MetagameTokenPackageBundle } from '../../dto/metagames.types';
import { MetaGamesEventType } from '../../dto/metagames.types';
import { devError } from '../../utils/log';
import type { IPackage } from '../app/types';
import { isMetagameTokenPackageBundles } from '../metagames/helpers';
import { isMetaGameFeatureBundleItem } from '../rewards/helpers';
import type { ClaimableReward } from '../ui/types';

import type { BingoCoinType } from './types';

export function hasFreeDaubInRewards(rewards: ClaimableReward[]): boolean {
  return rewards.some((pkg) =>
    pkg.bundleItems?.some(
      (item) => isMetaGameFeatureBundleItem(item) && item.bundlePayload.featureType === 'bingo_free_daub',
    ),
  );
}

export function detectFreeDaubCoinTypeFromRewards(rewards: ClaimableReward[]): 'GC' | 'CASH' | undefined {
  const { hasGc, hasCash } = rewards
    .flatMap((pkg) => pkg.bundleItems ?? [])
    .reduce(
      (acc, item) => {
        if (!isMetaGameFeatureBundleItem(item) || item.bundlePayload.featureType !== 'bingo_free_daub') return acc;
        return {
          hasGc: acc.hasGc || item.bundlePayload.coinType === 'GC',
          hasCash: acc.hasCash || item.bundlePayload.coinType === 'CASH',
        };
      },
      { hasGc: false, hasCash: false },
    );

  if (!hasGc && !hasCash) return undefined;
  if (hasGc && hasCash) return undefined;
  return hasGc ? 'GC' : 'CASH';
}

interface BingoBallCall {
  ballNumber: number;
  callOrder: number;
  callNumberBeforeCorrection?: number;
}

export const isBingoCardsPackageBundles = (
  packageBundles: MetagameTokenPackageBundle[],
): packageBundles is MetagameTokenPackageBundle[] => {
  return (
    packageBundles.length > 0 &&
    packageBundles.every((bundle) => bundle.bundlePayload.currencyName === MetaGamesEventType.BINGO)
  );
};

export function isBingoCardsPackage(pkg: Partial<IPackage>): pkg is MetagameTokenPackageBundle {
  return Boolean(
    typeof pkg === 'object' &&
      !!pkg &&
      'packageBundles' in pkg &&
      Array.isArray(pkg.packageBundles) &&
      pkg.packageBundles.length > 0 &&
      isMetagameTokenPackageBundles(pkg.packageBundles) &&
      isBingoCardsPackageBundles(pkg.packageBundles),
  );
}

export function getBingoCoinTypeFromSocketData(socketData: MetagameResultSocketData): BingoCoinType {
  if (socketData.amounts.CASH > 0 || socketData.bonusAmounts.CASH > 0) {
    return 'CASH';
  }
  return 'GC';
}

export function getRewardFromBingoSocketData(socketData: MetagameResultSocketData): number | null {
  let baseAmount = 0;
  let bonusAmount = 0;

  if (socketData.amounts.CASH > 0) {
    baseAmount = socketData.amounts.CASH;
    bonusAmount = socketData.bonusAmounts.CASH;
  } else if (socketData.amounts.GC > 0) {
    baseAmount = socketData.amounts.GC;
    bonusAmount = socketData.bonusAmounts.GC;
  } else {
    devError('BingoStore: No currency found for socket data', socketData);
    return null;
  }

  const totalAmount = baseAmount + bonusAmount;

  return totalAmount;
}

/**
 * Creates a bingo game ball calls array with sequential callOrder
 * @param ballNumbers - Array of ball numbers to call (1-75)
 * @param corrections - Optional array of corrections, where each correction specifies:
 *   - callOrder: the order position where the correction occurs
 *   - incorrectBallNumber: the ball number that was incorrectly called
 * @returns Array of bingo ball calls with sequential callOrder
 */
export const createBingoGameBallCalls = (
  ballNumbers: number[],
  corrections?: Array<{ callOrder: number; incorrectBallNumber: number }>,
): BingoBallCall[] => {
  const correctionsMap = new Map<number, number>();
  if (corrections) {
    corrections.forEach((correction) => {
      correctionsMap.set(correction.callOrder, correction.incorrectBallNumber);
    });
  }

  return ballNumbers.map((ballNumber, index) => {
    const callOrder = index + 1;
    const callNumberBeforeCorrection = correctionsMap.get(callOrder);

    const ballCall: BingoBallCall = {
      ballNumber,
      callOrder,
    };

    if (callNumberBeforeCorrection !== undefined) {
      ballCall.callNumberBeforeCorrection = callNumberBeforeCorrection;
    }

    return ballCall;
  });
};
